// Customizable Area Start
import React, { Component, createRef } from "react";
import { Box, IconButton, Typography, styled } from "@mui/material";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import PauseRoundedIcon from "@mui/icons-material/PauseRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import MessageBubbleComponent from "./MessageBubbleComponent.web";

type Props = {
  audioUrl: string;
  time: string;
  date: string;
  senderId: string;
  loggedInUserId: string;
  isSeen: boolean;
};

type State = {
  isPlaying: boolean;
  progress: number;
  duration: number;
};

const formatTime = (seconds: number) => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
};

const AudioWrapper = styled(Box)({
  maxWidth: "288px",
  width: "100%",
  display: "flex",
  flexDirection: "column",
  gap: 8,
});

const ControlsRow = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: 16,
  width: "100%",
});

const PlayButton = styled(IconButton)({
  backgroundColor: "#652784",
  color: "#ffffff",
  padding: 8,
  flexShrink: 0,
  "&:hover": {
    backgroundColor: "#552072",
  },
});

const ProgressBarWrapper = styled(Box)({
  position: "relative",
  flexGrow: 1,
  height: 6,
  borderRadius: 3,
  backgroundColor: "#e0e0e0",
  cursor: "pointer",
});

const ProgressBar = styled(Box)<{ width: number }>(({ width }) => ({
  position: "absolute",
  height: "100%",
  backgroundColor: "#652784",
  borderRadius: 3,
  width: `${width}%`,
}));

const DurationText = styled(Typography)({
  fontSize: "0.75rem",
  color: "#652784",
});

const DownloadButton = styled(IconButton)({
  padding: "6px",
  color: "#652784",
});

class AudioMessage extends Component<Props, State> {
  audioRef = createRef<HTMLAudioElement>();

  state: State = {
    isPlaying: false,
    progress: 0,
    duration: 0,
  };

  componentDidMount() {
    const audio = this.audioRef.current;
    if (!audio) return;

    audio.addEventListener("timeupdate", this.updateProgress);
    audio.addEventListener("loadedmetadata", this.updateDuration);
    audio.addEventListener("ended", this.handleAudioEnded);
  }

  componentWillUnmount() {
    const audio = this.audioRef.current;
    if (!audio) return;

    audio.removeEventListener("timeupdate", this.updateProgress);
    audio.removeEventListener("loadedmetadata", this.updateDuration);
    audio.removeEventListener("ended", this.handleAudioEnded);
  }

  handleAudioEnded = () => {
    this.setState({ isPlaying: false });
  };

  updateProgress = () => {
    const audio = this.audioRef.current;
    if (audio) {
      this.setState({ progress: audio.currentTime });
    }
  };

  updateDuration = () => {
    const audio = this.audioRef.current;
    if (audio) {
      this.setState({ duration: audio.duration });
    }
  };

  togglePlay = () => {
    const audio = this.audioRef.current;
    if (!audio) return;

    if (this.state.isPlaying) {
      audio.pause();
    } else {
      audio.play();
    }

    this.setState((prevState) => ({ isPlaying: !prevState.isPlaying }));
  };

  handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const { duration } = this.state;
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const percentage = clickX / rect.width;
    const newTime = percentage * duration;

    if (this.audioRef.current) {
      this.audioRef.current.currentTime = newTime;
    }
  };

  handleDownload = () => {
    const link = document.createElement("a");
    link.href = this.props.audioUrl;
    link.download = "";
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  render() {
    const { audioUrl, time, date, senderId, loggedInUserId, isSeen } =
      this.props;

    const { isPlaying, progress, duration } = this.state;
    const progressWidth = (progress / duration) * 100 || 0;

    return (
      <MessageBubbleComponent
        senderId={senderId}
        loggedInUserId={loggedInUserId}
        time={time}
        date={date}
        isSeen={isSeen}
      >
        <audio ref={this.audioRef} src={audioUrl} preload="metadata" />
        <AudioWrapper>
          <ControlsRow>
            <PlayButton onClick={this.togglePlay}>
              {isPlaying ? <PauseRoundedIcon /> : <PlayArrowRoundedIcon />}
            </PlayButton>

            <ProgressBarWrapper onClick={this.handleProgressClick}>
              <ProgressBar width={progressWidth} />
            </ProgressBarWrapper>

            <DownloadButton onClick={this.handleDownload}>
              <DownloadRoundedIcon />
            </DownloadButton>
          </ControlsRow>

          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <DurationText>
              {formatTime(progress)} / {formatTime(duration)}
            </DurationText>
          </Box>
        </AudioWrapper>
      </MessageBubbleComponent>
    );
  }
}

export default AudioMessage;
// Customizable Area End